// src/hooks/useInventory.ts

import {useState, useEffect, useCallback} from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import {firestore} from "../services/firebase/config";
import InventoryService, {
  InventoryItem,
  InventoryStatistics,
} from "../services/firebase/inventory";

export interface UseInventoryReturn {
  // Data
  items: InventoryItem[];
  statistics: InventoryStatistics | null;
  loading: boolean;
  error: string | null;

  // Operations
  addItem: (
    characterId: string,
    item: Omit<InventoryItem, "id" | "characterId" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  removeItem: (itemId: string, quantity?: number) => Promise<void>;
  updateItem: (
    itemId: string,
    updates: Partial<
      Pick<
        InventoryItem,
        | "equipped"
        | "equippedSlot"
        | "attunedBy"
        | "quantity"
        | "description"
        | "name"
      >
    >
  ) => Promise<void>;
  equipItem: (itemId: string, slot: string) => Promise<void>;
  unequipItem: (itemId: string) => Promise<void>;
  attuneToItem: (itemId: string, characterId: string) => Promise<void>;
  breakAttunement: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;

  // Utility functions
  getItemsByType: (type: InventoryItem["type"]) => InventoryItem[];
  getItemsByRarity: (rarity: InventoryItem["rarity"]) => InventoryItem[];
  getEquippedItems: () => InventoryItem[];
  getAttunedItems: (characterId: string) => InventoryItem[];
  getEquippedItemsBySlot: () => Record<string, InventoryItem[]>;
  calculateTotalWeight: () => number;

  // Validation
  validateItem: (item: Partial<InventoryItem>) => {
    isValid: boolean;
    errors: string[];
  };

  // Clear error
  clearError: () => void;
}

export const useInventory = (characterId?: string): UseInventoryReturn => {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [statistics, setStatistics] = useState<InventoryStatistics | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time inventory listener
  useEffect(() => {
    if (!characterId) {
      setItems([]);
      setStatistics(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(firestore, "inventory"),
      where("characterId", "==", characterId),
      orderBy("type"),
      orderBy("name")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const inventoryItems = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as InventoryItem[];

        setItems(inventoryItems);

        // Calculate statistics
        const totalWeight =
          InventoryService.calculateTotalWeight(inventoryItems);
        const equippedItems = inventoryItems.filter((item) => item.equipped);
        const attunedItems = inventoryItems.filter(
          (item) => item.attunedBy === characterId
        );

        setStatistics({
          totalItems: inventoryItems.length,
          totalWeight,
          equippedItems: equippedItems.length,
          attunedItems: attunedItems.length,
          maxAttunement: 3,
        });

        setLoading(false);
      },
      (error) => {
        // Error listening to inventory - handled by error state
        setError(error.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [characterId]);

  // Add item to inventory
  const addItem = useCallback(
    async (
      characterId: string,
      item: Omit<
        InventoryItem,
        "id" | "characterId" | "createdAt" | "updatedAt"
      >
    ) => {
      try {
        setError(null);
        await InventoryService.addItem(characterId, item);
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    []
  );

  // Remove item from inventory
  const removeItem = useCallback(
    async (itemId: string, quantity: number = 1) => {
      try {
        setError(null);
        await InventoryService.removeItem(itemId, quantity);
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    []
  );

  // Update item in inventory
  const updateItem = useCallback(
    async (
      itemId: string,
      updates: Partial<
        Pick<
          InventoryItem,
          | "equipped"
          | "equippedSlot"
          | "attunedBy"
          | "quantity"
          | "description"
          | "name"
        >
      >
    ) => {
      try {
        setError(null);
        await InventoryService.updateItem(itemId, updates);
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    []
  );

  // Equip item
  const equipItem = useCallback(async (itemId: string, slot: string) => {
    try {
      setError(null);
      await InventoryService.equipItem(itemId, slot);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Unequip item
  const unequipItem = useCallback(async (itemId: string) => {
    try {
      setError(null);
      await InventoryService.unequipItem(itemId);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Attune to item
  const attuneToItem = useCallback(
    async (itemId: string, characterId: string) => {
      try {
        setError(null);
        await InventoryService.attuneToItem(itemId, characterId);
      } catch (err: any) {
        // Don't set error state for attunement failures - these are handled by toast notifications
        // in the calling component. Just re-throw the error.
        throw err;
      }
    },
    []
  );

  // Break attunement
  const breakAttunement = useCallback(async (itemId: string) => {
    try {
      setError(null);
      await InventoryService.breakAttunement(itemId);
    } catch (err: any) {
      // Don't set error state for attunement failures - these are handled by toast notifications
      // in the calling component. Just re-throw the error.
      throw err;
    }
  }, []);

  // Update quantity
  const updateQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      try {
        setError(null);
        await InventoryService.updateQuantity(itemId, quantity);
      } catch (err: any) {
        setError(err.message);
        throw err;
      }
    },
    []
  );

  // Utility functions
  const getItemsByType = useCallback(
    (type: InventoryItem["type"]) => {
      return InventoryService.getItemsByType(items, type);
    },
    [items]
  );

  const getItemsByRarity = useCallback(
    (rarity: InventoryItem["rarity"]) => {
      return InventoryService.getItemsByRarity(items, rarity);
    },
    [items]
  );

  const getEquippedItems = useCallback(() => {
    return items.filter((item) => item.equipped);
  }, [items]);

  const getAttunedItems = useCallback(
    (characterId: string) => {
      return InventoryService.getAttunedItems(items, characterId);
    },
    [items]
  );

  const getEquippedItemsBySlot = useCallback(() => {
    return InventoryService.getEquippedItemsBySlot(items);
  }, [items]);

  const calculateTotalWeight = useCallback(() => {
    return InventoryService.calculateTotalWeight(items);
  }, [items]);

  const validateItem = useCallback((item: Partial<InventoryItem>) => {
    return InventoryService.validateItem(item);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Data
    items,
    statistics,
    loading,
    error,

    // Operations
    addItem,
    removeItem,
    updateItem,
    equipItem,
    unequipItem,
    attuneToItem,
    breakAttunement,
    updateQuantity,

    // Utility functions
    getItemsByType,
    getItemsByRarity,
    getEquippedItems,
    getAttunedItems,
    getEquippedItemsBySlot,
    calculateTotalWeight,

    // Validation
    validateItem,

    // Clear error
    clearError,
  };
};

// Hook for a specific character's inventory
export const useCharacterInventory = (characterId: string) => {
  return useInventory(characterId);
};

// Hook for inventory operations without real-time data
export const useInventoryOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addItem = useCallback(
    async (
      characterId: string,
      item: Omit<
        InventoryItem,
        "id" | "characterId" | "createdAt" | "updatedAt"
      >
    ) => {
      try {
        setLoading(true);
        setError(null);
        await InventoryService.addItem(characterId, item);
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const removeItem = useCallback(
    async (itemId: string, quantity: number = 1) => {
      try {
        setLoading(true);
        setError(null);
        await InventoryService.removeItem(itemId, quantity);
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateItem = useCallback(
    async (
      itemId: string,
      updates: Partial<
        Pick<
          InventoryItem,
          | "equipped"
          | "equippedSlot"
          | "attunedBy"
          | "quantity"
          | "description"
          | "name"
        >
      >
    ) => {
      try {
        setLoading(true);
        setError(null);
        await InventoryService.updateItem(itemId, updates);
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    addItem,
    removeItem,
    updateItem,
    clearError,
  };
};
